import { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Button, ConfirmDialog, EmptyState, MenuButton, Spinner, navigate, useLocation, useToast } from '@dugzi/ui';
import { ArrowLeft, LogOut, MoreHorizontal, Pencil, Trash2, UserPlus } from 'lucide-react';
import { api } from '../lib/api';
import { letterOf, localNames, rowLabel, useData } from '../lib/data';
import type { Person } from '../lib/types';
import type { Route } from '../App';
import { PersonCard } from './PersonCard';
import { PersonEditor } from './PersonEditor';
import { RenameBook } from './RenameBook';
import { WriteToOffer } from './WriteToOffer';

/** One list of people (everyone, a book, a label or a household) with an A–Z index, and the open card. */
export function PeopleView({ route }: { route: Route }) {
  const { path, search } = useLocation();
  const { books, labels, households, version, reload, me } = useData();
  const toast = useToast();
  const q = new URLSearchParams(search);
  const open = q.get('p');
  const adding = q.get('new') === '1';
  const editing = q.get('edit') === '1';
  const term = q.get('q') ?? '';
  const [people, setPeople] = useState<Person[] | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);

  const book = route.kind === 'book' ? books.find((b) => b.id === route.id) : undefined;
  const household = route.kind === 'household' ? households.find((h) => h.id === route.id) : undefined;
  const title = route.kind === 'book' ? book?.name ?? 'Address book'
    : route.kind === 'label' ? route.label
    : route.kind === 'household' ? household?.name ?? 'Household'
    : 'All contacts';

  useEffect(() => {
    const p = new URLSearchParams();
    if (term) p.set('q', term);
    if (route.kind === 'book') p.set('book', route.id);
    if (route.kind === 'label') p.set('label', route.label);
    if (route.kind === 'household') p.set('household', route.id);
    let live = true;
    api<{ people: Person[] }>(`/api/people?${p}`).then((r) => { if (live) setPeople(localNames(r.people)); }).catch((e) => toast.show(e.message));
    return () => { live = false; };
  }, [term, version, route.kind === 'book' ? route.id : route.kind === 'label' ? route.label : route.kind === 'household' ? route.id : '']); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    const g: [string, Person[]][] = [];
    for (const p of people ?? []) {
      const l = letterOf(p);
      const last = g[g.length - 1];
      if (last && last[0] === l) last[1].push(p); else g.push([l, [p]]);
    }
    return g;
  }, [people]);

  const go = (params: Record<string, string | null>) => {
    const n = new URLSearchParams(search);
    for (const [k, v] of Object.entries(params)) { if (v === null) n.delete(k); else n.set(k, v); }
    const s = n.toString();
    navigate(path + (s ? `?${s}` : ''));
  };
  const selected = people?.find((p) => p.id === open) ?? null;
  const canAdd = route.kind !== 'book' || book?.role !== 'view';
  const jump = (letter: string) => listRef.current?.querySelector<HTMLElement>(`[data-letter="${letter}"] .person-row`)?.focus();

  const bookMenu = book && [
    book.role === 'owner' && !book.isDefault && { label: 'Rename', icon: <Pencil aria-hidden />, onSelect: () => setRenaming(true) },
    book.role === 'owner' && !book.isDefault && { label: 'Delete this address book', icon: <Trash2 aria-hidden />, danger: true, onSelect: () => setDeleting(true) },
    book.role !== 'owner' && { label: `Leave ${book.name}`, icon: <LogOut aria-hidden />, onSelect: async () => {
      try { await api(`/api/books/${book.id}/leave`, { body: {} }); await reload(); navigate('/'); toast.show(`You left ${book.name}.`); } catch (e) { toast.show((e as Error).message); }
    } },
  ];

  return (
    <div className={`split ${open || adding ? 'has-card' : ''}`}>
      <section className="list" aria-labelledby="list-title">
        <div className="view__head">
          <div>
            <h1 id="list-title">{term ? `“${term}”` : title}</h1>
            <p className="view__sub" data-testid="list-sub">
              {people === null ? ' ' : `${people.length} ${people.length === 1 ? 'person' : 'people'}`}
              {household?.address && ` · ${household.address}`}
            </p>
          </div>
          <div className="view__tools">
            {book && bookMenu && bookMenu.some(Boolean) && <MenuButton label={`More for ${book.name}`} icon={<MoreHorizontal aria-hidden />} variant="ghost" size="sm" items={bookMenu} />}
            {canAdd && <Button size="sm" variant="primary" icon={<UserPlus aria-hidden />} onClick={() => go({ new: '1', p: null, edit: null })} data-testid="add-person">Add contact</Button>}
          </div>
        </div>
        {route.kind === 'all' && !term && <WriteToOffer />}
        {people === null ? <div className="view__loading"><Spinner label="Loading contacts" /></div>
          : people.length === 0 ? (
            term ? <EmptyState title="Nobody matches">Try part of a name, an email address, a phone number or a label.</EmptyState>
              : <EmptyState scene={route.kind === 'all' ? 'contacts.first-circle' : undefined} title={route.kind === 'all' ? 'Only the people you choose' : 'Nobody here yet'}
                action={canAdd ? <Button icon={<UserPlus aria-hidden />} onClick={() => go({ new: '1' })}>Add contact</Button> : undefined}>
                {route.kind === 'all' ? 'Dugzi never adds anyone by itself. Add someone, or import a .vcf file from your phone or old address book.' : 'People you add here stay in your address book.'}
              </EmptyState>
          ) : (
            <div className="list__body">
              <ul className="people" ref={listRef} aria-label={title} data-testid="people">
                {groups.map(([letter, ps]) => (
                  <li key={letter} data-letter={letter} className="people__group">
                    <h2 className="people__letter" aria-hidden>{letter}</h2>
                    <ul className="people__rows">
                      {ps.map((p) => (
                        <li key={p.id}>
                          <button type="button" className={`person-row ${p.id === open ? 'is-open' : ''}`} aria-label={rowLabel(p)} aria-current={p.id === open ? 'true' : undefined}
                            onClick={() => go({ p: p.id, new: null, edit: null })} data-testid="person-row" data-name={p.display}>
                            <Avatar name={p.display} size={36} />
                            <span className="person-row__text">
                              <span className="person-row__name">{p.display}</span>
                              <span className="person-row__sub">{p.howWeMet || p.org || p.emails[0]?.value || p.phones[0]?.value || ''}</span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              {groups.length > 5 && (
                <nav className="az" aria-label="Jump to a letter">
                  {groups.map(([l]) => <button key={l} type="button" onClick={() => jump(l)} aria-label={`Jump to ${l}`}>{l}</button>)}
                </nav>
              )}
            </div>
          )}
      </section>
      {(selected || adding) && (
        <section className="card-pane" aria-label={adding ? 'New contact' : selected!.display}>
          <Button className="card-pane__back" variant="ghost" size="sm" icon={<ArrowLeft aria-hidden />} onClick={() => go({ p: null, new: null, edit: null })}>Back to the list</Button>
          {adding || (editing && selected)
            ? <PersonEditor key={selected?.id ?? 'new'} person={adding ? null : selected} defaultBook={route.kind === 'book' && book?.role !== 'view' ? route.id : me.defaultBook}
              presetLabel={route.kind === 'label' ? route.label : undefined}
              onDone={async (id) => { await reload(); go({ p: id, new: null, edit: null }); }} onCancel={() => go({ new: null, edit: null })} />
            : <PersonCard person={selected!} labels={labels} onEdit={() => go({ edit: '1' })} onGone={() => { reload(); go({ p: null }); }} />}
        </section>
      )}
      {open && people && !selected && !adding && <section className="card-pane"><EmptyState title="That person isn’t here">They may have been deleted, or they’re in another list.</EmptyState></section>}
      {book && renaming && <RenameBook book={book} onClose={() => setRenaming(false)} />}
      {book && <ConfirmDialog open={deleting} onClose={() => setDeleting(false)} title={`Delete ${book.name}?`} confirmLabel="Delete" icon={<Trash2 aria-hidden size={16} />}
        onConfirm={async () => { try { await api(`/api/books/${book.id}`, { method: 'DELETE' }); setDeleting(false); await reload(); navigate('/'); toast.show(`${book.name} deleted.`); } catch (e) { toast.show((e as Error).message); } }}>
        <p className="dz-muted">The {book.count} {book.count === 1 ? 'person' : 'people'} in it go too, for everyone it’s shared with. Export it first if you want a copy.</p>
      </ConfirmDialog>}
    </div>
  );
}
